import { Box, Typography } from '@mui/material';

/**
 * Upcoming eBooks section displaying future releases
 *
 * @returns {React.ReactElement} Upcoming eBooks section component
 */
function UpcomingEBooksSection() {
  const upcomingBooks = [
    {
      id: 'modernize',
      title: 'Modernization: An Easy Guide to Innovation',
      status: 'Coming Soon',
      statusColor: 'var(--color-text-muted)',
      description:
        'A practical roadmap for organizational modernization and digital transformation. This guide simplifies complex innovation processes into actionable steps that any organization can follow.',
      topics: [
        'Building innovation culture',
        'Technology modernization strategies',
        'Change management best practices',
        'Measuring innovation success',
      ],
      cornerStyle: 'right-left', // top-right, bottom-left
    },
    {
      id: 'dec-2025',
      title: 'No is not a Strategy: GenAI Governance & Guardrails',
      status: 'Expected Release: December 2025',
      statusColor: 'var(--color-primary)',
      description:
        'Navigate the complex landscape of Generative AI governance with practical strategies for responsible implementation. This comprehensive guide addresses the critical balance between innovation and risk management in enterprise AI deployments.',
      topics: [
        'Establishing AI governance frameworks',
        'Risk assessment and mitigation strategies',
        'Compliance and regulatory considerations',
        'Building organizational AI capabilities',
      ],
      cornerStyle: 'left-right', // top-left, bottom-right
    },
    {
      id: 'march-2026',
      title: 'Cloud Not Dead: Why Public Cloud',
      status: 'Expected Release: March 2026',
      statusColor: 'var(--color-primary)',
      description:
        'Dispelling myths and misconceptions about public cloud adoption. This guide provides compelling evidence and real-world case studies demonstrating why public cloud remains the strategic choice for modern enterprises.',
      topics: [
        'Public cloud advantages and ROI analysis',
        'Addressing security and compliance concerns',
        'Migration strategies and best practices',
        'Future-proofing your cloud strategy',
      ],
      cornerStyle: 'left-right', // top-left, bottom-right
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      {upcomingBooks.map((book) => (
        <Box
          key={book.id}
          id={book.id}
          sx={{
            backgroundColor: 'var(--color-card)',
            borderRadius: '12px',
            p: { xs: 3, md: 5 },
            mb: 6,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            border: '1px solid var(--color-border)',
            // Dynamic corner styling
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -1,
              left: book.cornerStyle === 'left-right' ? -1 : 'auto',
              right: book.cornerStyle === 'right-left' ? -1 : 'auto',
              width: '60px',
              height: '60px',
              borderTop: '4px solid var(--color-primary)',
              borderLeft:
                book.cornerStyle === 'left-right'
                  ? '4px solid var(--color-primary)'
                  : 'none',
              borderRight:
                book.cornerStyle === 'right-left'
                  ? '4px solid var(--color-primary)'
                  : 'none',
              borderRadius: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -1,
              right: book.cornerStyle === 'left-right' ? -1 : 'auto',
              left: book.cornerStyle === 'right-left' ? -1 : 'auto',
              width: '60px',
              height: '60px',
              borderBottom: '4px solid var(--color-primary)',
              borderRight:
                book.cornerStyle === 'left-right'
                  ? '4px solid var(--color-primary)'
                  : 'none',
              borderLeft:
                book.cornerStyle === 'right-left'
                  ? '4px solid var(--color-primary)'
                  : 'none',
              borderRadius: 0,
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: 'var(--color-heading)', fontWeight: 600, mb: 2 }}
          >
            {book.title}
          </Typography>
          <Typography
            sx={{
              color: book.statusColor,
              fontWeight: 600,
              mb: 3,
            }}
          >
            {book.status}
          </Typography>
          <Typography
            paragraph
            sx={{
              color: 'var(--color-card-text)',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            {book.description}
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'var(--color-primary)', fontWeight: 600, mb: 2 }}
          >
            {book.id === 'modernize'
              ? 'Future Topics:'
              : book.id === 'dec-2025'
                ? "What You'll Learn:"
                : 'Key Topics:'}
          </Typography>
          <Box
            component="ul"
            sx={{
              color: 'var(--color-card-text)',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              pl: 3,
            }}
          >
            {book.topics.map((topic, index) => (
              <li key={index}>{topic}</li>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default UpcomingEBooksSection;
